import i18next from 'i18next'
import { slugify } from './utils'

type ParentPageData = {
  AbsolutePath: string
  Path: string
} & (
  | { ContentType: 'Centra pagina' }
  | { ContentType: 'Spaarne pagina'; Language: { Code: string }; Parent: ParentPageData | null }
)

type SharepointPageDataForUrls = {
  AbsolutePath: string
} & (
  | { ContentType: 'Homepage'; Language: { Code: string } | null }
  | { ContentType: 'Centra pagina'; Path: string }
  | {
      ContentType: 'Afdeling pagina'
      Department: { Title: string; Centrum: { Title: string } | null } | null
      Path: string
    }
  | {
      ContentType: 'Sub-afdeling pagina'
      Subdepartment: { Department: { Title: string; Centrum: { Title: string } | null } | null, Centrum: { Title: string } | null }| null
      Path: string
    }
  | {
      ContentType: 'Sub-centra pagina'
      Subcentra: { Centra: { Title: string; } | null } | null
      Path: string
    }
  | { ContentType: 'Spaarne pagina'; Language: { Code: string }; Parent: ParentPageData | null; Path: string }
  | { ContentType: 'Agenda pagina'; Path: string }
  | { ContentType: 'News pagina'; Path: string }
  | { ContentType: 'Locatie pagina'; Path: string }
  | { ContentType: 'Specialist pagina'; Path: string }
  | { ContentType: 'Onderzoek pagina'; Path: string }
  | { ContentType: 'Wachttijden pagina' }
  | { ContentType: 'Gegenereerde pagina'; Path: string }
  | { ContentType: 'Vrienden pagina'; Path: string}
  | { ContentType: 'Folder pagina'; Path: string}
  | { ContentType: 'Dossier pagina'; Path: string}
)

export const cmsPageUrl = (data: SharepointPageDataForUrls, prefixLanguage: boolean = true): string => {
  if (data != null && data.AbsolutePath != null) {
    return data.AbsolutePath
  } else {
    return cmsPageUrlFallback(data, prefixLanguage);
  }
}

const cmsPageUrlFallback = (data: SharepointPageDataForUrls, prefixLanguage: boolean = true): string => {
  if (data != undefined && data != null) {
    switch (data.ContentType) {
      case 'Homepage':
        return data.Language?.Code === 'en' ? '/en' : '/'
      case 'Centra pagina':
        return `/afdelingen-en-centra${data.Path.toLowerCase()}`
      case 'Afdeling pagina':
        return `/afdelingen-en-centra${
          data.Department?.Centrum ? `/${slugify(data.Department.Centrum.Title)}` : ''
        }${data.Path.toLowerCase()}`
      case 'Sub-afdeling pagina':
        return `/afdelingen-en-centra${
          data.Subdepartment?.Centrum? `/${slugify(data.Subdepartment.Centrum.Title)}` :
          (data.Subdepartment?.Department?.Centrum ? `/${slugify(data.Subdepartment.Department.Centrum.Title)}` : '')
        }${data.Subdepartment?.Department ? `/${slugify(data.Subdepartment.Department.Title)}` : ''}${data.Path}`
      case 'Sub-centra pagina':
        return `/afdelingen-en-centra${
          data.Subcentra && data.Subcentra.Centra ? `/${slugify(data.Subcentra.Centra.Title)}` : ''
        }${data.Path}`
      case 'Agenda pagina':
        return `/agenda${data.Path.toLowerCase()}`
      case 'News pagina':
        return `/nieuws${data.Path.toLowerCase()}`
      case 'Specialist pagina':
        return `/specialisten${data.Path.toLowerCase()}`
      case 'Spaarne pagina':
        return `${data.Language?.Code === 'en' && prefixLanguage ? '/en' : ''}${
          data.Parent ? `${cmsPageUrl(data.Parent, false)}${data.Path}` : data.Path.toLowerCase()
        }`
      case 'Locatie pagina':
        return `/contact${data.Path}`
      case 'Onderzoek pagina':
        return `/wetenschappelijke-onderzoeken${data.Path}`
      case 'Wachttijden pagina':
        return '/wachttijden'
      case 'Gegenereerde pagina':
        return data.Path
      case 'Vrienden pagina':
        return `/vrienden/${data.Path}`
      case 'Folder pagina':
        return `/folders/${data.Path}`
      case 'Dossier pagina':
        return `/dossiers/${data.Path}`
    }
  } else {
    return "";
  }
}

// below is meant for links in the footer and header, not all types of urls are supported there

type SharepointPageDataForUrlsSimpler =
  | { ContentType: 'Homepage' }
  | { ContentType: 'Centra pagina'; Path: string }
  | { ContentType: 'Sub-centra pagina'; Path: string }
  | { ContentType: 'Afdeling pagina'; Path: string }
  | { ContentType: 'Sub-afdeling pagina'; Path: string }
  | { ContentType: 'Spaarne pagina'; Path: string }
  | { ContentType: 'Agenda pagina'; Path: string }
  | { ContentType: 'News pagina'; Path: string }
  | { ContentType: 'Locatie pagina'; Path: string }
  | { ContentType: 'Specialist pagina'; Path: string }
  | { ContentType: 'Onderzoek pagina'; Path: string }
  | { ContentType: 'Wachttijden pagina' }
  | { ContentType: 'Gegenereerde pagina'; Path: string }
  | { ContentType: 'Vrienden pagina'; Path: string}
  | { ContentType: 'Folder pagina'; Path: string}
  | { ContentType: 'Dossier pagina'; Path: string}
export const cmsPageUrlSimpler = (data: SharepointPageDataForUrlsSimpler): string => {
  switch (data.ContentType) {
    case 'Homepage':
      return i18next.language === 'en' ? '/en' : '/'
    case 'Vrienden pagina':
      return `/vrienden${data.Path.toLocaleLowerCase()}`
    case 'Centra pagina':
      return `/afdelingen-en-centra${data.Path.toLowerCase()}`
    case 'Sub-centra pagina':
      return `/afdelingen-en-centra${data.Path.toLowerCase()}`
    case 'Afdeling pagina':
      return `/afdelingen-en-centra${data.Path.toLowerCase()}`
    case 'Sub-afdeling pagina':
      return `/afdelingen-en-centra${data.Path.toLowerCase()}`
    case 'Agenda pagina':
      return `/agenda${data.Path.toLowerCase()}`
    case 'News pagina':
      return `/nieuws${data.Path.toLowerCase()}`
    case 'Specialist pagina':
      return `/specialisten${data.Path.toLowerCase()}`
    case 'Spaarne pagina':
      return `${i18next.language === 'en' ? '/en' : ''}${data.Path.toLowerCase()}`
    case 'Locatie pagina':
      return `/contact${data.Path}`
    case 'Onderzoek pagina':
      return `/wetenschappelijke-onderzoeken${data.Path}`
    case 'Wachttijden pagina':
      return '/wachttijden'
    case 'Folder pagina':
      return `/folders${data.Path.toLowerCase()}`
    case 'Dossier pagina':
      return `/dossiers${data.Path.toLowerCase()}`
    case 'Gegenereerde pagina':
      return data.Path
  }
}
